import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  UserPlus, Gift, Link as LinkIcon, Copy, CheckCircle, Clock, Users,
  TrendUp, CurrencyGbp, ArrowRight
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function ReferralProgrammePage() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.is_platform_admin;
  const [myCode, setMyCode] = useState(null);
  const [history, setHistory] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [codeRes, histRes] = await Promise.all([
        API.get("/referrals/my-code"),
        API.get("/referrals/history"),
      ]);
      setMyCode(codeRes.data);
      setHistory(histRes.data);

      if (isPlatformAdmin) {
        const { data } = await API.get("/analytics/referrals");
        setAdminStats(data);
      }
    } catch {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCopy = () => {
    if (!myCode?.code) return;
    navigator.clipboard.writeText(myCode.code);
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const { data } = await API.post("/referrals/apply", { code: applyCode.trim().toUpperCase() });
      toast.success(data.message || "Referral applied!");
      setApplyCode("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply referral code");
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading referral programme...</div>;

  const referralUrl = myCode?.code ? `${window.location.origin}/signup?ref=${myCode.code}` : "";

  return (
    <div data-testid="referral-programme-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Gift size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Referral Programme</h1>
          <p className="text-xs text-muted-foreground">Invite others and earn free subscription days</p>
        </div>
      </div>

      {/* My Referral Code Section */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="my-referral-code">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon size={18} className="text-amber-500" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Your Referral Code</h3>
          </div>
          {myCode?.code ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-muted rounded-md px-4 py-3 text-center">
                  <span className="text-lg font-bold font-mono tracking-widest" data-testid="referral-code-value">{myCode.code}</span>
                </div>
                <Button variant="outline" size="icon" onClick={handleCopy} data-testid="copy-referral-code">
                  {copied ? <CheckCircle size={18} className="text-emerald-500" /> : <Copy size={18} />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mb-2">Share this code or the link below:</p>
              <div className="flex items-center gap-2">
                <Input value={referralUrl} readOnly className="text-[11px] h-8 bg-muted" data-testid="referral-url" />
                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(referralUrl); toast.success("Link copied!"); }} data-testid="copy-referral-url">
                  <Copy size={14} />
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No referral code available</p>
          )}
        </div>

        <div className="border border-border rounded-lg p-5 bg-card" data-testid="referral-rewards">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={18} className="text-emerald-500" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Your Rewards</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-2xl font-bold font-[Chivo] text-emerald-600" data-testid="successful-referrals">{myCode?.successful_referrals || 0}</p>
              <p className="text-[10px] text-muted-foreground">Successful Referrals</p>
            </div>
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-2xl font-bold font-[Chivo] text-amber-600" data-testid="total-reward-days">{history?.total_rewards_days || 0}</p>
              <p className="text-[10px] text-muted-foreground">Bonus Days Earned</p>
            </div>
          </div>
          <div className="mt-3 p-2 rounded bg-primary/5 border border-primary/10">
            <p className="text-[11px] text-muted-foreground">
              <strong className="text-foreground">How it works:</strong> You get <span className="text-emerald-600 font-semibold">30 free days</span> for each referral. The person you refer gets <span className="text-blue-600 font-semibold">7 bonus days</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Apply a Referral Code */}
      <div className="border border-border rounded-lg p-5 bg-card mb-6" data-testid="apply-referral-section">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus size={18} className="text-blue-500" weight="duotone" />
          <h3 className="text-sm font-semibold font-[Chivo]">Have a Referral Code?</h3>
        </div>
        <div className="flex items-center gap-2 max-w-md">
          <Input
            value={applyCode}
            onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
            placeholder="Enter referral code"
            className="font-mono uppercase tracking-wider h-9"
            data-testid="apply-referral-input"
          />
          <Button onClick={handleApply} disabled={applying || !applyCode.trim()} size="sm" data-testid="apply-referral-btn">
            {applying ? "Applying..." : "Apply"}
            {!applying && <ArrowRight size={14} className="ml-1" />}
          </Button>
        </div>
      </div>

      {/* Referral History */}
      {history?.referrals?.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden mb-6" data-testid="referral-history">
          <div className="px-4 py-3 bg-muted/30 border-b border-border">
            <h3 className="text-sm font-semibold font-[Chivo]">Referral History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/10">
                  <th className="px-3 py-2 text-left font-semibold">Referred Tenant</th>
                  <th className="px-3 py-2 text-center font-semibold">Status</th>
                  <th className="px-3 py-2 text-right font-semibold">Reward</th>
                  <th className="px-3 py-2 text-right font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.referrals.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-border hover:bg-muted/10">
                    <td className="px-3 py-2 font-medium">{r.referred_tenant_id?.slice(0, 8)}...</td>
                    <td className="px-3 py-2 text-center">
                      <Badge className={r.status === "converted" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}>
                        {r.status === "converted" ? "Converted" : r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-600 font-medium">+{r.reward_days} days</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Platform Admin: Referral Analytics */}
      {isPlatformAdmin && adminStats && (
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="admin-referral-analytics">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-violet-500" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Platform Referral Analytics</h3>
            <Badge variant="outline" className="text-[9px] ml-auto">Admin</Badge>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-xl font-bold font-[Chivo]" data-testid="admin-total-codes">{adminStats.total_referral_codes}</p>
              <p className="text-[10px] text-muted-foreground">Total Codes Created</p>
            </div>
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-xl font-bold font-[Chivo] text-emerald-600" data-testid="admin-total-referrals">{adminStats.total_successful_referrals}</p>
              <p className="text-[10px] text-muted-foreground">Successful Referrals</p>
            </div>
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-xl font-bold font-[Chivo] text-amber-600" data-testid="admin-total-reward-days">{adminStats.total_reward_days_given}</p>
              <p className="text-[10px] text-muted-foreground">Total Reward Days Given</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
